/**
 * toast — THE user-facing notification primitive (E-5, 2026-07-07).
 *
 * Replaces the 100+ blocking `alert()` calls that were the app's de-facto error UX.
 * Deliberately framework-free (plain DOM): callable from React components, services,
 * and viewer event handlers alike, with no provider/context wiring. Colors come from
 * the theme's CSS custom properties with safe fallbacks, so light/dark both work.
 *
 * confirm() dialogs are NOT covered here — blocking confirmation is a deliberate UX.
 */

type ToastKind = 'error' | 'success';

const KIND_STYLE: Record<ToastKind, { border: string; icon: string }> = {
  error: { border: 'var(--theme-error, #e5484d)', icon: '⚠️' },
  success: { border: 'var(--theme-success, #46a758)', icon: '✅' },
};

let container: HTMLDivElement | null = null;

const ensureContainer = (): HTMLDivElement => {
  if (container && document.body.contains(container)) return container;
  container = document.createElement('div');
  container.setAttribute('data-mabat-toasts', '');
  Object.assign(container.style, {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 'var(--dt-z-toast)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'center',
    pointerEvents: 'none',
  } as CSSStyleDeclaration);
  document.body.appendChild(container);
  return container;
};

const show = (message: string, kind: ToastKind, durationMs: number): void => {
  // Non-browser context (tests/SSR): degrade to console so callers never crash.
  if (typeof document === 'undefined') {
    // eslint-disable-next-line no-console
    console[kind === 'error' ? 'error' : 'log'](`[toast:${kind}]`, message);
    return;
  }

  const host = ensureContainer();
  const toast = document.createElement('div');
  toast.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  Object.assign(toast.style, {
    maxWidth: 'min(560px, 90vw)',
    padding: '10px 16px',
    borderRadius: '8px',
    borderInlineStart: `4px solid ${KIND_STYLE[kind].border}`,
    background: 'var(--theme-surface, #1f2733)',
    color: 'var(--theme-text-primary, #f5f7fa)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
    fontSize: '13px',
    lineHeight: '1.45',
    pointerEvents: 'auto',
    cursor: 'pointer',
    opacity: '0',
    transition: 'opacity 160ms ease, transform 160ms ease',
    transform: 'translateY(6px)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  } as CSSStyleDeclaration);
  toast.textContent = `${KIND_STYLE[kind].icon} ${message}`;

  const dismiss = () => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(6px)';
    window.setTimeout(() => toast.remove(), 200);
  };
  toast.addEventListener('click', dismiss);

  host.appendChild(toast);
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });
  window.setTimeout(dismiss, durationMs);
};

export const showErrorToast = (message: string): void => show(message, 'error', 6500);
export const showSuccessToast = (message: string): void => show(message, 'success', 3500);
